import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import {
  ReportTemplateDTOSchema,
  type TemplateField,
} from "@arna-med/shared";
import { useBackendAuthenticated } from "@/hooks/useBackendAuthenticated";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

const TemplatesResponseSchema = z.object({
  success: z.boolean(),
  data: z.array(ReportTemplateDTOSchema),
});

const TemplateResponseSchema = z.object({
  success: z.boolean(),
  data: ReportTemplateDTOSchema,
});

type ReportTemplateDTO = z.infer<typeof ReportTemplateDTOSchema>;

export function useTemplates(specialityId: string | null | undefined) {
  const backend = useBackendAuthenticated();
  return useQuery({
    queryKey: ["templates", specialityId ?? null],
    queryFn: async () => {
      const json = await backend({
        method: "GET",
        url: `/api/v1/templates?specialityId=${specialityId}`,
      });
      return TemplatesResponseSchema.parse(json).data;
    },
    enabled: !!specialityId,
  });
}

/**
 * The primary-visit questionnaire.
 *
 * Starting a consultation no longer involves choosing one, so the app asks for
 * it by name. Cached for the session: it is one shared row that changes only
 * when the seed is re-run.
 */
export function usePrimaryVisitTemplate() {
  const backend = useBackendAuthenticated();
  return useQuery({
    queryKey: ["template", "primary-visit"],
    queryFn: async () => {
      const json = await backend({
        method: "GET",
        url: "/api/v1/templates/primary-visit",
      });
      return TemplateResponseSchema.parse(json).data;
    },
    staleTime: Infinity,
    retry: false,
  });
}

export function useNewConsultationTemplates({
  specialityId,
  enabled,
}: {
  specialityId?: string | null;
  enabled: boolean;
}) {
  const backend = useBackendAuthenticated();
  return useQuery({
    queryKey: ["templates", specialityId ?? null],
    queryFn: async () => {
      const url = specialityId
        ? `/api/v1/templates?specialityId=${specialityId}`
        : "/api/v1/templates";
      const json = await backend({ method: "GET", url });
      return TemplatesResponseSchema.parse(json).data.filter((t) => t.isActive);
    },
    enabled,
  });
}

const SlimTemplateSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  isActive: z.boolean().optional(),
});

const SlimTemplatesResponseSchema = z.object({
  success: z.boolean(),
  data: z.array(SlimTemplateSchema),
});

export function useTemplatesForFilter(options?: { enabled?: boolean }) {
  const backend = useBackendAuthenticated();
  return useQuery({
    queryKey: ["templates-for-filter"],
    queryFn: async () => {
      const json = await backend({ method: "GET", url: "/api/v1/templates?slim=true" });
      return SlimTemplatesResponseSchema.parse(json).data;
    },
    enabled: options?.enabled ?? false,
    staleTime: 5 * 60 * 1000, // templates rarely change, cache for 5 min
  });
}

export function useTemplate(id: string | null | undefined) {
  const backend = useBackendAuthenticated();
  return useQuery({
    queryKey: ["template", id],
    queryFn: async () => {
      const json = await backend({
        method: "GET",
        url: `/api/v1/templates/${id}`,
      });
      return TemplateResponseSchema.parse(json).data;
    },
    enabled: !!id,
  });
}

export function useSaveTemplate(
  template: ReportTemplateDTO | null,
  specialityId: string,
) {
  const backend = useBackendAuthenticated();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: (data: {
      name: string;
      nameRu?: string;
      nameKz?: string;
      fields: TemplateField[];
      llmPrompt?: string;
      mohFormId?: string | null;
      isGeneral?: boolean;
      reportStructureId?: string | null;
    }) =>
      template
        ? backend({
            method: "PATCH",
            url: `/api/v1/templates/${template.id}`,
            data,
          })
        : backend({
            method: "POST",
            url: "/api/v1/templates",
            data: { ...data, specialityId, isActive: true },
          }),
    onSuccess: (response: unknown) => {
      queryClient.invalidateQueries({ queryKey: ["templates", specialityId] });
      const warnings = (response as { warnings?: { fieldLabel: string }[] })?.warnings;
      if (warnings && warnings.length > 0) {
        toast.warning(
          t("templates.mohWarnings", { count: warnings.length }),
          { duration: 6000 },
        );
      } else {
        toast.success(
          template
            ? t("templates.templateSaved")
            : t("templates.templateCreated"),
        );
      }
    },
  });
}

export function useToggleTemplate(specialityId: string | null) {
  const backend = useBackendAuthenticated();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      backend({
        method: "PATCH",
        url: `/api/v1/templates/${id}`,
        data: { isActive },
      }),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ["templates", specialityId],
      }),
  });
}

export function useDeleteTemplate(specialityId: string | null) {
  const backend = useBackendAuthenticated();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      backend({ method: "DELETE", url: `/api/v1/templates/${id}` }),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ["templates", specialityId],
      }),
  });
}
